import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronRight, Clock, Search, Truck, X } from "lucide-react";
import { useProducts, useNotifications, useVendors } from "@/lib/queries";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import { useNow } from "@/lib/use-now";
import { ChefNav } from "@/components/ChefNav";
import { QuantityStepper } from "@/components/QuantityStepper";
import { Button } from "@/components/ui/button";
import {
  cn,
  formatCountdown,
  formatDeliveryDay,
  formatTime,
} from "@/lib/utils";
import type { ProductSummary, Vendor } from "@/lib/types";

// Accent- and case-insensitive form for matching ("Crème" ~ "creme").
const fold = (s: string) =>
  s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const IN_ORDER = "__in_order__";

// Under this much time left, a vendor's deadline is flagged as closing soon.
const SOON_MS = 2 * 3_600_000;

// Order Catalog — the chef's main screen, organised like the buyer's guide:
// pick a vendor (each shows a countdown to its order deadline), then browse its
// items by category. Type to filter (every word must match the name or
// category), Enter jumps to the first result's quantity, "/" focuses search
// from anywhere.
export function OrderCatalog() {
  const { user } = useAuth();
  const { data, isLoading } = useProducts();
  const { data: vendorData } = useVendors(user?.locationIds[0]);
  const { data: notifications } = useNotifications();
  const { lines, setQuantity, setFormat, count } = useCart();
  const now = useNow();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<string | null>(null);
  const [openCats, setOpenCats] = useState<Set<string>>(new Set());
  const searchRef = useRef<HTMLInputElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  // Category headers pin right under the (sticky) search toolbar.
  const [stickyTop, setStickyTop] = useState(0);
  useEffect(() => {
    const measure = () => {
      const el = toolbarRef.current;
      if (el) setStickyTop(64 + el.offsetHeight);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);
  const listRef = useRef<HTMLDivElement>(null);

  const allProducts = data?.data ?? [];
  // Soonest deadline first: that's the order the chef should place next.
  const vendors = useMemo(
    () =>
      [...(vendorData?.data ?? [])].sort(
        (a, b) =>
          (a.orderBy ? Date.parse(a.orderBy) : Infinity) -
            (b.orderBy ? Date.parse(b.orderBy) : Infinity) ||
          a.name.localeCompare(b.name),
      ),
    [vendorData],
  );
  const vendor =
    vendors.find((v) => v.id === params.get("vendor")) ?? vendors[0];
  const selectVendor = (id: string) => {
    setParams({ vendor: id }, { replace: true });
    setFilter(null);
    setOpenCats(new Set());
  };
  const products = useMemo(
    () => allProducts.filter((p) => p.supplierId === vendor?.id),
    [allProducts, vendor?.id],
  );
  const inOrderByVendor = (id: string) =>
    [...lines.values()].filter((l) => l.supplierId === id && l.quantity > 0)
      .length;
  const tokens = fold(query).split(/\s+/).filter(Boolean);

  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of products)
      if (p.category) counts.set(p.category, (counts.get(p.category) ?? 0) + 1);
    return [...counts].sort((a, b) => a[0].localeCompare(b[0]));
  }, [products]);

  const results = useMemo(() => {
    const matches = products.filter((p) => {
      if (filter === IN_ORDER && !(lines.get(p.id)?.quantity ?? 0))
        return false;
      if (filter && filter !== IN_ORDER && p.category !== filter) return false;
      const hay = fold(`${p.name} ${p.category ?? ""}`);
      return tokens.every((t) => hay.includes(t));
    });
    if (!tokens.length)
      return matches.sort(
        (a, b) =>
          (a.category ?? "").localeCompare(b.category ?? "") ||
          a.name.localeCompare(b.name),
      );
    // Searching: names that start with the first word come first.
    const score = (p: ProductSummary) => {
      const name = fold(p.name);
      return name.startsWith(tokens[0])
        ? 0
        : name.split(/\s+/).some((w) => w.startsWith(tokens[0]))
          ? 1
          : 2;
    };
    return matches.sort(
      (a, b) => score(a) - score(b) || a.name.localeCompare(b.name),
    );
  }, [products, tokens.join(" "), filter, lines]);

  // "/" focuses search from anywhere (unless already typing in a field).
  useEffect(() => {
    // Desktop only: on a phone, focusing would pop the keyboard on arrival.
    if (window.matchMedia("(pointer: fine)").matches) searchRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (e.key === "/" && tag !== "INPUT" && tag !== "TEXTAREA") {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function focusFirstQuantity() {
    listRef.current
      ?.querySelector<HTMLInputElement>('input[aria-label="Quantity"]')
      ?.focus();
  }

  const grouped = !tokens.length && filter !== IN_ORDER;
  // Browsing the whole catalog: sections are collapsible (collapsed by default).
  // A search or a filter always shows its matches.
  const collapsible = grouped && filter === null;
  const isOpen = (cat: string) => !collapsible || openCats.has(cat);
  const toggleCat = (cat: string) =>
    setOpenCats((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  const inOrderByCat = (cat: string) =>
    products.filter(
      (p) =>
        (p.category ?? "Other") === cat && (lines.get(p.id)?.quantity ?? 0) > 0,
    ).length;
  const inOrderCount = vendor ? inOrderByVendor(vendor.id) : 0;

  return (
    <div className="min-h-screen bg-canvas">
      <ChefNav notifications={notifications?.data ?? []} />

      <div className="mx-auto max-w-5xl px-4 pb-32 pt-6 sm:pt-8 md:px-6">
        <h1 className="page-title max-sm:text-3xl" data-testid="catalog-title">
          Order Catalog
        </h1>
        <p className="page-sub mt-1 hidden sm:block">
          Pick a vendor, then your items — just like the buyer&apos;s guide.
          If a vendor runs out, we switch to a backup supplier and let you
          know.
        </p>

        <VendorStrip
          vendors={vendors}
          selectedId={vendor?.id}
          onSelect={selectVendor}
          inOrder={inOrderByVendor}
          now={now}
        />

        {/* Search + filters stay pinned while scrolling the list. */}
        <div
          ref={toolbarRef}
          className="sticky top-16 z-20 -mx-4 mt-4 border-b border-line bg-canvas/95 px-4 pb-3 pt-3 backdrop-blur sm:pb-4 sm:pt-4 md:-mx-6 md:px-6"
        >
          {vendor && <DeadlineBanner vendor={vendor} now={now} />}
          <div className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-faint" />
            <input
              ref={searchRef}
              type="search"
              enterKeyHint="search"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  focusFirstQuantity();
                }
                if (e.key === "Escape") setQuery("");
              }}
              placeholder={
                vendor ? `Search ${vendor.name}…` : "Search items or categories…"
              }
              aria-label="Search the catalog"
              className="h-12 w-full rounded-card border border-line bg-surface-2 pl-12 pr-14 text-base text-ink sm:h-14 sm:pr-24 placeholder:text-faint focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
            <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-2">
              {query ? (
                <button
                  type="button"
                  onClick={() => {
                    setQuery("");
                    searchRef.current?.focus();
                  }}
                  className="rounded p-1.5 text-muted hover:bg-surface-3 hover:text-ink"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : (
                <kbd className="hidden rounded border border-line px-2 py-0.5 font-mono text-xs text-faint sm:inline">
                  /
                </kbd>
              )}
            </div>
          </div>

          <div
            className="no-scrollbar -mx-4 mt-3 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0"
            role="tablist"
            aria-label="Filter"
          >
            <Chip
              active={filter === null}
              onClick={() => setFilter(null)}
              label="All"
              count={products.length}
            />
            <Chip
              active={filter === IN_ORDER}
              onClick={() => setFilter(filter === IN_ORDER ? null : IN_ORDER)}
              label="In my order"
              count={inOrderCount}
              accent
            />
            <span className="mx-1 w-px shrink-0 bg-line" aria-hidden />
            {categories.map(([cat, n]) => (
              <Chip
                key={cat}
                active={filter === cat}
                onClick={() => setFilter(filter === cat ? null : cat)}
                label={cat}
                count={n}
              />
            ))}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between text-xs text-faint">
          <span>
            {isLoading
              ? "Loading…"
              : `${results.length} item${results.length === 1 ? "" : "s"}`}
            <span className="hidden sm:inline">
              {tokens.length > 0
                ? " — Enter jumps to the first quantity, Enter again returns to search"
                : " — press / to search"}
            </span>
          </span>
          {collapsible && categories.length > 0 && (
            <button
              type="button"
              onClick={() =>
                setOpenCats(
                  openCats.size === categories.length
                    ? new Set()
                    : new Set(categories.map(([c]) => c)),
                )
              }
              className="text-muted underline-offset-4 hover:text-ink hover:underline"
            >
              {openCats.size === categories.length
                ? "Collapse all"
                : "Expand all"}
            </button>
          )}
        </div>

        <div
          ref={listRef}
          className="-mx-4 mt-2 border-y border-line bg-surface sm:mx-0 sm:rounded-card sm:border-x"
          onKeyDown={(e) => {
            // Enter in a quantity box: back to search for the next item.
            const t = e.target as HTMLElement;
            if (
              e.key === "Enter" &&
              t.getAttribute("aria-label") === "Quantity"
            ) {
              e.preventDefault();
              searchRef.current?.focus();
              searchRef.current?.select();
            }
          }}
        >
          {results.length === 0 && !isLoading && (
            <div className="px-6 py-16 text-center text-muted">
              {filter === IN_ORDER ? (
                "Nothing in your order yet."
              ) : (
                <>No items match “{query}”.</>
              )}
            </div>
          )}
          {results.map((p, i) => {
            const line = lines.get(p.id);
            const qty = line?.quantity ?? 0;
            const needsFormat = p.formats.length > 0 && !line?.formatId;
            const newGroup =
              grouped && (i === 0 || results[i - 1].category !== p.category);
            return (
              <Fragment key={p.id}>
                {newGroup &&
                  (() => {
                    const cat = p.category ?? "Other";
                    const open = isOpen(cat);
                    const picked = inOrderByCat(cat);
                    const total = categories.find(([c]) => c === cat)?.[1] ?? 0;
                    return collapsible ? (
                      <button
                        type="button"
                        onClick={() => toggleCat(cat)}
                        aria-expanded={open}
                        className={cn(
                          "flex min-h-14 w-full items-center gap-3 border-b border-line px-4 py-4 text-left transition-colors hover:bg-surface-2 sm:px-5 sm:first:rounded-t-card",
                          open && "sticky z-10 bg-surface-2",
                        )}
                        style={open ? { top: stickyTop } : undefined}
                      >
                        <ChevronRight
                          className={cn(
                            "h-4 w-4 text-muted transition-transform",
                            open && "rotate-90",
                          )}
                        />
                        <span className="font-head text-base font-extrabold uppercase tracking-wide text-ink">
                          {cat}
                        </span>
                        <span className="font-mono text-xs text-faint">
                          {total}
                        </span>
                        {picked > 0 && (
                          <span className="ml-auto rounded-full bg-brand/15 px-2.5 py-0.5 text-xs text-brand">
                            {picked} in order
                          </span>
                        )}
                      </button>
                    ) : (
                      <div
                        className="sticky z-10 border-b border-line bg-surface-2 px-5 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-muted first:rounded-t-card"
                        style={{ top: stickyTop }}
                      >
                        {cat}
                      </div>
                    );
                  })()}
                {isOpen(p.category ?? "Other") && (
                  <div
                    className={cn(
                      "flex items-center gap-3 border-b border-l-[3px] border-b-line py-3 pl-4 pr-3 transition-colors last:border-b-0 hover:bg-surface-2/60 sm:gap-4 sm:px-5",
                      qty > 0
                        ? "border-l-brand bg-brand/[0.06]"
                        : "border-l-transparent",
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-ink">
                        <Highlight text={p.name} tokens={tokens} />
                      </div>
                      <div className="mt-0.5 flex items-center gap-2 text-xs text-muted">
                        {!grouped && p.category && (
                          <span className="text-faint">{p.category}</span>
                        )}
                        {!grouped && p.category && p.formats.length === 0 && (
                          <span className="text-faint">·</span>
                        )}
                        {p.formats.length === 0 && (
                          <span>per {p.unitOfMeasure.toLowerCase()}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-2 sm:flex-row sm:items-center sm:gap-3">
                      {p.formats.length > 0 && (
                        <select
                          aria-label={`Format for ${p.name}`}
                          value={line?.formatId ?? ""}
                          onChange={(e) => setFormat(p, e.target.value)}
                          className={cn(
                            "h-11 max-w-[11rem] rounded-card border bg-surface-2 px-2 text-sm text-ink focus:border-brand focus:outline-none sm:h-8 sm:max-w-[13rem]",
                            needsFormat
                              ? "border-dashed border-line text-muted"
                              : "border-line",
                          )}
                        >
                          <option value="" disabled>
                            Choose format…
                          </option>
                          {p.formats.map((f) => (
                            <option key={f.id} value={f.id}>
                              {f.label}
                            </option>
                          ))}
                        </select>
                      )}
                      <QuantityStepper
                        value={qty}
                        disabled={needsFormat}
                        onChange={(next) => setQuantity(p, next)}
                      />
                    </div>
                  </div>
                )}
              </Fragment>
            );
          })}
        </div>
      </div>

      {/* Fixed bottom bar with running count + Review Order CTA */}
      {count > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur">
          <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-3 sm:py-4 md:px-6">
            <button
              type="button"
              onClick={() => setFilter(IN_ORDER)}
              className="text-sm text-muted hover:text-ink"
            >
              <span className="font-head text-lg font-extrabold text-brand">
                {count}
              </span>{" "}
              item
              {count === 1 ? "" : "s"} selected
            </button>
            <Link to="/app/review">
              <Button>Review Order</Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

// Vendors as tabs in a wrapping grid (no sideways scrolling), each with a
// countdown to its next order deadline.
function VendorStrip({
  vendors,
  selectedId,
  onSelect,
  inOrder,
  now,
}: {
  vendors: Vendor[];
  selectedId: string | undefined;
  onSelect: (id: string) => void;
  inOrder: (id: string) => number;
  now: number;
}) {
  return (
    <div
      className="mt-4 grid grid-cols-2 gap-2 sm:mt-6 sm:grid-cols-3 lg:grid-cols-5"
      role="tablist"
      aria-label="Vendors"
    >
      {vendors.map((v) => {
        const active = v.id === selectedId;
        const left = v.orderBy ? Date.parse(v.orderBy) - now : null;
        const soon = left !== null && left < SOON_MS;
        const picked = inOrder(v.id);
        return (
          <button
            key={v.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onSelect(v.id)}
            className={cn(
              "flex min-w-0 flex-col items-start gap-0.5 rounded-card border px-3 py-2 text-left transition-colors sm:gap-1 sm:px-3.5 sm:py-2.5",
              active
                ? "border-brand bg-brand/10"
                : "border-line bg-surface hover:bg-surface-2",
            )}
          >
            <span className="flex w-full items-center gap-2">
              <span
                className={cn(
                  "truncate font-head text-[13px] font-extrabold uppercase tracking-wide sm:text-sm",
                  active ? "text-brand" : "text-ink",
                )}
              >
                {v.name}
              </span>
              {picked > 0 && (
                <span className="ml-auto rounded-full bg-brand px-1.5 font-mono text-[11px] text-[#04181d]">
                  {picked}
                </span>
              )}
            </span>
            <span
              className={cn(
                "flex items-center gap-1 text-xs",
                soon ? "font-semibold text-amber" : "text-muted",
              )}
            >
              <Clock className="h-3.5 w-3.5" />
              {left === null ? "No delivery set" : `${formatCountdown(left)} left`}
            </span>
            <span className="text-[11px] text-faint">
              Delivery {formatDeliveryDay(v.deliveryDate)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// "Order within 2h 48m for delivery Wed, Oct 30" for the selected vendor. A
// warning tint when closing soon, never an alarm.
function DeadlineBanner({ vendor, now }: { vendor: Vendor; now: number }) {
  if (!vendor.orderBy) {
    return (
      <div className="mb-3 text-sm text-muted">
        {vendor.name} has no delivery days set for your location.
      </div>
    );
  }
  const left = Date.parse(vendor.orderBy) - now;
  const soon = left < SOON_MS;
  return (
    <div
      data-testid="deadline-banner"
      className={cn(
        "mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-card border px-3.5 py-2 text-sm",
        soon
          ? "border-amber/40 bg-amber/10 text-ink"
          : "border-line bg-surface text-muted",
      )}
    >
      <span className="flex items-center gap-1.5">
        <Clock className={cn("h-4 w-4", soon ? "text-amber" : "text-brand")} />
        Order within{" "}
        <strong className={soon ? "text-amber" : "text-ink"}>
          {formatCountdown(left)}
        </strong>
      </span>
      <span className="flex items-center gap-1.5">
        <Truck className="h-4 w-4 text-brand" />
        for delivery{" "}
        <strong className="text-ink">
          {formatDeliveryDay(vendor.deliveryDate)}
        </strong>
      </span>
      <span className="text-xs text-faint">
        Deadline {formatTime(vendor.orderBy)}
      </span>
    </div>
  );
}

function Chip({
  active,
  onClick,
  label,
  count,
  accent,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  accent?: boolean;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors",
        active
          ? "bg-brand text-[#04181d]"
          : accent
            ? "border border-brand/40 text-brand hover:bg-brand/10"
            : "bg-surface-2 text-muted hover:text-ink",
      )}
    >
      {label}
      <span
        className={cn(
          "font-mono text-xs",
          active ? "text-[#04181d]/70" : "text-faint",
        )}
      >
        {count}
      </span>
    </button>
  );
}

// Bold the parts of `text` matching any search token (accent-insensitive).
function Highlight({ text, tokens }: { text: string; tokens: string[] }) {
  if (!tokens.length) return <>{text}</>;
  const folded = fold(text);
  const marks = new Array<boolean>(text.length).fill(false);
  for (const t of tokens) {
    let from = 0;
    let at: number;
    while ((at = folded.indexOf(t, from)) !== -1) {
      for (let i = at; i < at + t.length; i++) marks[i] = true;
      from = at + t.length;
    }
  }
  const parts: Array<{ s: string; m: boolean }> = [];
  for (let i = 0; i < text.length; i++) {
    const last = parts[parts.length - 1];
    if (last && last.m === marks[i]) last.s += text[i];
    else parts.push({ s: text[i], m: marks[i] });
  }
  return (
    <>
      {parts.map((p, i) =>
        p.m ? (
          <mark key={i} className="rounded-sm bg-brand/25 text-ink">
            {p.s}
          </mark>
        ) : (
          <span key={i}>{p.s}</span>
        ),
      )}
    </>
  );
}
